import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import {
	IVolunteerAttendance,
	VolunteerAttendanceStatus,
	buildContactFilter,
	useVolunteerAttendanceQuery,
	useVolunteerRolesQuery,
	useVolunteerSummaryQuery,
} from '../../../../../queries/Volunteer';
import { MultiColorPillProps, Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Button } from '../../../../components-v2/Button';
import { ROUTES } from '../../../../constants/routes';
import { formatCurrency } from '../../../../pages/AdvancedReporting/helpers/formatCurrency';
import { resolveZone } from '../../../../pages/Volunteers/shiftScheduling';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { VolunteerIcon } from '../../../svgs/icons/VolunteerIcon';
import { DonatedTimeValueHelp, formatVolunteerHours } from '../../../volunteering/DonatedTimeValueHelp';
import { DataBoardRow, DataBoardTable } from '../../DataBoardTable';
import { styleSheet } from './styles';

const STATUS_LABELS: Record<VolunteerAttendanceStatus, string> = {
	[VolunteerAttendanceStatus.Expected]: 'Expected',
	[VolunteerAttendanceStatus.CheckedIn]: 'Checked in',
	[VolunteerAttendanceStatus.CheckedOut]: 'Checked out',
	[VolunteerAttendanceStatus.NoShow]: 'No-show',
	[VolunteerAttendanceStatus.Cancelled]: 'Cancelled',
};

const STATUS_COLORS: Record<VolunteerAttendanceStatus, MultiColorPillProps['color']> = {
	[VolunteerAttendanceStatus.Expected]: 'yellow',
	[VolunteerAttendanceStatus.CheckedIn]: 'blue',
	[VolunteerAttendanceStatus.CheckedOut]: 'green',
	[VolunteerAttendanceStatus.NoShow]: 'red',
	[VolunteerAttendanceStatus.Cancelled]: 'gray',
};

// `date` is midnight on the day the server means, so reading it through a Date shifts it by the
// viewer's UTC offset and lands it on the wrong day. Take the leading YYYY-MM-DD instead.
const formatAttendanceDate = (date: string | undefined) =>
	date ? moment(date.slice(0, 10), 'YYYY-MM-DD').format('MM/DD/YYYY') : '-';

// A row's activity can sit in any timezone and the expanded activity reference does not carry one,
// so scheduled times are shown in the viewer's zone, abbreviation included.
const formatShiftWindow = (row: IVolunteerAttendance) => {
	if (!row.scheduledStartAt) {
		return '-';
	}
	const zone = resolveZone();
	const start = moment.tz(row.scheduledStartAt, zone);
	if (!row.scheduledEndAt) {
		return `${start.format('h:mm A')} ${start.format('z')}`;
	}
	return `${start.format('h:mm A')} – ${moment.tz(row.scheduledEndAt, zone).format('h:mm A')} ${start.format('z')}`;
};

const attendanceDayKey = (row: IVolunteerAttendance) =>
	row.scheduledStartAt
		? moment.tz(row.scheduledStartAt, resolveZone()).format('YYYY-MM-DD')
		: (row.date?.slice(0, 10) ?? '');

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const ActivityName = ({ row }: { row: IVolunteerAttendance }) => (
	<span className={css(styleSheet.activityCell)}>
		{row.activity?.title ?? (row.volunteerActivityId ? 'Untitled activity' : 'Unknown activity')}
		{row.activity?.isArchived ? <Pill variant='label' color='gray' label='Archived' size='small' /> : null}
	</span>
);

const ActivityCell = ({ row, onClick }: { row: IVolunteerAttendance; onClick: (activityId: string) => void }) => {
	if (!row.volunteerActivityId) {
		return <span>{row.activity?.title ?? 'Unknown activity'}</span>;
	}
	const activityId = row.volunteerActivityId;
	return (
		<span className={css(styleSheet.activityCell)}>
			<Button variant='link' styles={[styleSheet.activityLink]} onClick={() => onClick(activityId)}>
				{row.activity?.title ?? 'Untitled activity'}
			</Button>
			{row.activity?.isArchived ? <Pill variant='label' color='gray' label='Archived' size='small' /> : null}
		</span>
	);
};

export const EntityVolunteering = ({ entity }: { entity: Api.IEntity }) => {
	const history = useHistory();
	const filter = useMemo(() => buildContactFilter(entity.id ?? ''), [entity.id]);

	const attendanceQuery = useVolunteerAttendanceQuery({ filter, enabled: !!entity.id });
	const summaryQuery = useVolunteerSummaryQuery({ filter, enabled: !!entity.id });
	const rolesQuery = useVolunteerRolesQuery({ includeArchived: true, enabled: !!entity.id });

	const attendance = useMemo(
		() => attendanceQuery.data?.pages.flatMap(page => page.values) ?? [],
		[attendanceQuery.data]
	);

	const roleNamesById = useMemo(
		() => new Map((rolesQuery.data ?? []).map(role => [role.id, role.name])),
		[rolesQuery.data]
	);

	const nextShift = useMemo(() => {
		const today = moment().format('YYYY-MM-DD');
		// Rows arrive newest first, so the soonest upcoming shift is the last of the leading future block
		// rather than the first row.
		return attendance
			.filter(row => row.status === VolunteerAttendanceStatus.Expected && attendanceDayKey(row) >= today)
			.sort((a, b) => attendanceDayKey(a).localeCompare(attendanceDayKey(b)))[0];
	}, [attendance]);

	const onFetchNextBatch = () => {
		if (attendanceQuery.hasNextPage && !attendanceQuery.isFetchingNextPage) {
			attendanceQuery.fetchNextPage();
		}
	};

	const openActivity = (activityId: string) => history.push(`${ROUTES.volunteers.home}/${activityId}`);

	const summary = summaryQuery.data;
	const hasAttendance = attendance.length > 0;

	const rows: DataBoardRow[] = attendance.map(row => {
		const status = row.status ?? VolunteerAttendanceStatus.Expected;
		return {
			id: row.id,
			name: <ActivityName row={row} />,
			status: <Pill variant='label' color={STATUS_COLORS[status]} label={STATUS_LABELS[status]} />,
			fields: [
				formatAttendanceDate(row.date),
				(row.roleId && roleNamesById.get(row.roleId)) || '-',
				formatShiftWindow(row),
			],
			value: row.hours == null ? '-' : formatVolunteerHours(row.hours),
		};
	});

	const headerSummary =
		hasAttendance && (summary || nextShift) ? (
			<>
				{summary ? (
					<span className={css(styleSheet.summaryLine)}>
						{[
							`${formatVolunteerHours(summary.totalHours ?? 0)} across ${pluralize(summary.participationCount ?? 0, 'participation')}`,
							pluralize(summary.noShowCount ?? 0, 'no-show'),
							`${formatCurrency(summary.donatedTimeValue ?? 0)} donated time value`,
						].join(' · ')}
						<DonatedTimeValueHelp ratePerHour={summary.donatedTimeValueRatePerHour} />
					</span>
				) : null}
				{nextShift ? (
					<span className={css(styleSheet.summaryLine)}>
						{`Next shift: ${[
							formatAttendanceDate(nextShift.date),
							formatShiftWindow(nextShift),
							nextShift.roleId ? roleNamesById.get(nextShift.roleId) : undefined,
						]
							.filter(part => part && part !== '-')
							.join(' · ')} ·`}
						<ActivityCell row={nextShift} onClick={openActivity} />
					</span>
				) : null}
			</>
		) : undefined;

	return (
		<DataBoardTable
			icon={<VolunteerIcon fill={THEME.colors.primary.contrastText} />}
			iconColor='green'
			title='Volunteering'
			summary={headerSummary}
			nameLabel='Activity'
			statusLabel='Status'
			fieldLabels={['Date', 'Role', 'Shift']}
			valueLabel='Hours'
			rows={rows}
			// A disabled query sits at `isPending` forever, so the spinner has to key off a fetch in flight.
			isLoading={attendanceQuery.isLoading}
			// Without this the empty state would tell the viewer this contact has never volunteered when the
			// request simply failed — and the board-disabled case suppresses its toast, so there would be no
			// other signal at all.
			emptyText={attendanceQuery.isError ? 'Couldn’t load this contact’s volunteering.' : 'No volunteering yet.'}
		>
			{attendanceQuery.hasNextPage && !attendanceQuery.isFetchingNextPage ? (
				<Waypoint bottomOffset='-200px' onEnter={onFetchNextBatch} />
			) : null}
			{attendanceQuery.isFetchingNextPage ? (
				<div className={css(styleSheet.loadMore)}>
					<LoadingSpinner type='small' />
				</div>
			) : null}
		</DataBoardTable>
	);
};
